import {
  ClipType,
  Ethnicity,
  Gender,
  ModelType,
  Platform,
  VideoDuration,
  VideoType,
  VoiceType,
  AspectRatio,
  ChildAgeRange,
  CreatorPetsOwner,
} from '../gql/types'

export const videoTypeLocalizedString = (videoType: VideoType) => {
  switch (videoType) {
    case VideoType.howTo:
      return 'How to'
    case VideoType.unboxing:
      return 'Unboxing'
    case VideoType.testimonial:
      return 'Testimonial'
    case VideoType.upToTheCreator:
      return 'Up to the creator'
  }
}

export const videoDurationLocalizedString = (videoDuration: VideoDuration) => {
  switch (videoDuration) {
    case VideoDuration.quarter:
      // return '15-20 seconds'
      return '15-20 sec'
    case VideoDuration.half:
      // return '20-40 seconds'
      return '20-40 sec'
    case VideoDuration.minute:
      // return '40-60 seconds'
      return '40-60 sec'
    default:
      return null
  }
}

export const modelTypeLocalizedString = (modelType: ModelType) => {
  switch (modelType) {
    case ModelType.bodyFace:
      return 'Body & Face'
    case ModelType.handsOnly:
      return 'Hands only'
  }
}

export const platformLocalizedString = (platform: Platform) => {
  switch (platform) {
    case Platform.instagram:
      return 'Instagram'
    case Platform.tiktok:
      return 'TikTok'
  }
}

export const ethnicityLocalizedString = (ethnicity: Ethnicity) => {
  switch (ethnicity) {
    case Ethnicity.asian:
      return 'Asian / Pacific Islander'
    case Ethnicity.black:
      return 'Black or African American'
    case Ethnicity.hispanic:
      return 'Hispanic'
    case Ethnicity.indian:
      return 'American Indian or Alaskan Native'
    case Ethnicity.white:
      return 'White / Caucasian'
    case Ethnicity.other:
      return 'Other'
  }
}

export const UserSourceChannels = [
  'recommendation',
  'instagramAds',
  'facebookAds',
  'googleAds',
  'linkedInAds',
  'googleSearch',
  'bingSearch',
  'clipBlog',
  'reviewOnBlog',
  'other',
]

export const sourceChannelLocalizedString = (sourceChannel: string) => {
  switch (sourceChannel) {
    case 'recommendation':
      return 'Recommendation'
    case 'instagramAds':
      return 'Instagram Ads'
    case 'facebookAds':
      return 'Facebook Ads'
    case 'googleAds':
      return 'Google Ads'
    case 'linkedInAds':
      return 'LinkedIn Ads'
    case 'googleSearch':
      return 'Google Search'
    case 'bingSearch':
      return 'Bing Search'
    case 'clipBlog':
      return 'Clip blog'
    case 'reviewOnBlog':
      return 'Review on a blog, website, etc.'
    case 'other':
      return 'Other'
  }
}

export const ethnicityLocalizedStringInverse = (ethnicity): Ethnicity => {
  switch (ethnicity) {
    case 'Asian / Pacific Islander':
      return Ethnicity.asian
    case 'Black or African American':
      return Ethnicity.black
    case 'Hispanic':
      return Ethnicity.hispanic
    case 'American Indian or Alaskan Native':
      return Ethnicity.indian
    case 'White / Caucasian':
      return Ethnicity.white
    case 'Other':
      return Ethnicity.other
    default:
      return null
  }
}

export const petLocalizedString = (petsOwner: CreatorPetsOwner) => {
  switch (petsOwner) {
    case CreatorPetsOwner.noPets:
      return 'No pets'
    case CreatorPetsOwner.cat:
      return 'Cat'
    case CreatorPetsOwner.dog:
      return 'Dog'
    case CreatorPetsOwner.other:
      return 'Other pet'
    default:
      return null
  }
}

export const petLocalizedStringInverse = (
  petLocalized: string,
): CreatorPetsOwner => {
  switch (petLocalized) {
    case 'No pets':
      return CreatorPetsOwner.noPets
    case 'Cat':
      return CreatorPetsOwner.cat
    case 'Dog':
      return CreatorPetsOwner.dog
    case 'Other pet':
      return CreatorPetsOwner.other
    default:
      return null
  }
}

export const genderLocalizedString = (gender: Gender) => {
  switch (gender) {
    case Gender.female:
      return 'Female'
    case Gender.male:
      return 'Male'
    case Gender.other:
      return 'Other'
  }
}

export const genderLocalizedStringInverse = (gender): Gender => {
  switch (gender) {
    case 'Female':
      return Gender.female
    case 'Male':
      return Gender.male
    case 'Other':
      return Gender.other
    default:
      return null
  }
}

export const clipTypeLocalizedString = (type: ClipType) => {
  switch (type) {
    case ClipType.firstVersion:
      return `Main Video`
    case ClipType.editedVersion:
      return 'Edited Video'
    case ClipType.hook:
      return 'Variation'
    case ClipType.addon:
      return 'Add-on'
    case ClipType.otherFile:
      return 'Other'
    case ClipType.rawFile:
      return 'Raw File'
  }
}

export const clipTypeLocalizedStringInverse = (type): ClipType => {
  switch (type) {
    case 'Main Video':
      return ClipType.firstVersion
    case 'Edited Video':
      return ClipType.editedVersion
    case 'Variation':
      return ClipType.hook
    case 'Add-on':
      return ClipType.addon
    case 'Raw File':
      return ClipType.rawFile
    case 'Other':
      return ClipType.otherFile
    default:
      return null
  }
}

export const voiceTypeLocalizedString = (voiceType: VoiceType) => {
  switch (voiceType) {
    case VoiceType.overVoice:
      // return 'Voiceover / Talking to the camera'
      return 'Voiceover'
    case VoiceType.noVoice:
      return 'No Voice'
  }
}

export const voiceTypeLocalizedStringInverse = (voiceType): VoiceType => {
  switch (voiceType) {
    // case 'Voiceover / Talking to the camera':
    case 'Voiceover':
      return VoiceType.overVoice
    case 'No Voice':
      return VoiceType.noVoice
    default:
      return null
  }
}

export const aspectRatioLocalizedString = (aspectRatio: AspectRatio) => {
  switch (aspectRatio) {
    case AspectRatio.vertical:
      return 'Vertical(9:16)'
    case AspectRatio.square:
      return 'Square(1:1)'
    case AspectRatio.portrait:
      return 'Portrait(4:5)'
    case AspectRatio.landscape:
      return 'Landscape(16:9)'
  }
}

export const childAgeRangeLocalizedString = (childAgeRange: ChildAgeRange) => {
  switch (childAgeRange) {
    case ChildAgeRange.noChildren:
      return 'No children'
    case ChildAgeRange.upTo2:
      return '0-2 years'
    case ChildAgeRange.twoToSix:
      return '2-6 years'
    case ChildAgeRange.sixPlus:
      return '6+ years'
  }
}

export const childAgeRangeLocalizedStringInverse = (
  childAgeRange: string,
): ChildAgeRange => {
  switch (childAgeRange) {
    case 'No children':
      return ChildAgeRange.noChildren
    case '0-2 years':
      return ChildAgeRange.upTo2
    case '2-6 years':
      return ChildAgeRange.twoToSix
    case '6+ years':
      return ChildAgeRange.sixPlus
    default:
      return null
  }
}
